import React, { useState, ChangeEvent, useEffect, useRef } from 'react';
import { Grid, Button, TextField, Tooltip } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { ModalWrapper } from 'components/Modals';
import TokenIcon from 'assets/images/token.svg';
import { LoaderButton } from 'components/LoaderButton';
import { RootStore } from 'redux/store';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { TeamUsers } from 'redux/models/teamUsers';
import { NewPost } from 'redux/models/newPost';
import { sendTokens as sendTokensThunk } from 'redux/thunks';
import { resetSendTokensSuccess } from 'redux/actions';
import { NewSubpost } from 'redux/models/newSubpost';
import { sendSubpostTokens } from 'redux/thunks/team';
import { UserIdName } from 'redux/models/userIdName';
import { modalRandomMovement } from 'helpers/helper';
import './SendTokensModal.scss';
import { Alert } from '@material-ui/lab';
import { useEasterEggFound } from 'helpers/easterEgghelper';
import EASTER_EGG_IDS from 'helpers/easterEggConstants';

interface SendTokensProps {
	open: boolean;
	postId?: string;
	postRecipients?: UserIdName[];
	allowClickOutsideToClose?: boolean;
	onCloseClicked: () => void;
}

const SendTokensModal: React.FC<SendTokensProps> = (props: SendTokensProps) => {
	const dispatch = useDispatch();
	const userState = useSelector((state: RootStore) => state.user);
	const teamState = useSelector((state: RootStore) => state.team);
	const [recipients, setRecipients] = useState([] as UserIdName[]);
	const [initialRecipients, setInitialRecipients] = useState([] as UserIdName[]);
	const [tokens, setTokens] = useState(0);
	const [message, setMessage] = useState('');
	const [tokenError, setTokenError] = useState(false);
	const [recipientsError, setRecipientsError] = useState(false);
	const [teamUsers, setTeamUsers] = useState([] as UserIdName[]);
	const [iconMovement, setIconMovement] = useState(false);
	const [tooltipOpen, setToolTipOpen] = useState(false);
	const unacceptedUsers = useRef([] as string[]);
	const { open, postId, postRecipients, allowClickOutsideToClose, onCloseClicked } = props;
	const easterEggFound = useEasterEggFound(EASTER_EGG_IDS.CATCH_ME_IF_YOU_CAN);

	const setTokenValue = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const value = event.target.value;

		if (tokenError) {
			setTokenError(false);
		}

		if (!value || parseInt(value) < 0) {
			setTokens(0);
		} else if (value?.length < 4) {
			setTokens(parseInt(value));
		}
	};

	const setRecipientValue = (selectedUsers: UserIdName[]) => {
		if (recipientsError) {
			setRecipientsError(false);
		}

		setRecipients(selectedUsers);
	};

	const setMessageValue = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		setMessage(event.target.value);
	};

	const sendTokensValid = () => {
		let error = false;
		if (tokens === 0) {
			setTokenError(true);
			error = true;
		}

		if (recipients?.length < 1) {
			setRecipientsError(true);
			error = true;
		}

		if (error) {
			return false;
		}

		return true;
	};

	const sendTokens = () => {
		if (!sendTokensValid()) {
			return;
		}

		if (postId?.length) {
			const newSubpost: NewSubpost = {
				comment: message,
				teamId: userState.selectedTeam?.teamId || '',
				postId: postId,
				tokens: tokens,
				users: recipients,
			};

			dispatch(sendSubpostTokens(newSubpost));
		} else {
			const newPost: NewPost = {
				comment: message,
				teamId: userState.selectedTeam?.teamId || '',
				tokens: tokens,
				users: recipients,
			};

			dispatch(sendTokensThunk(newPost));
		}
	};

	const loaderButtonProps = {
		loading: teamState.sendingTokens,
		onHandleClick: sendTokens,
	};

	const setTokenRecipients = (selectedUsers: any) => {
		let acceptedUsers: UserIdName[] = [];
		unacceptedUsers.current = [];

		selectedUsers.forEach((recipient: UserIdName) => {
			if (!recipient.hasAcceptedTerms) {
				const updatedUnacceptedUsers: string[] = [...unacceptedUsers.current];
				updatedUnacceptedUsers.push(recipient.name);
				unacceptedUsers.current = updatedUnacceptedUsers;
			} else {
				acceptedUsers.push(recipient);
			}
		});

		setRecipientValue(acceptedUsers);
	};

	const toggleIconMovement = () => {
		setIconMovement(!iconMovement);

		if (!iconMovement) {
			easterEggFound();
		}
	};

	useEffect(() => {
		if (teamState.sendingTokensSuccess) {
			dispatch(resetSendTokensSuccess());
			onCloseClicked();
		}
	}, [unacceptedUsers, teamState.sendingTokensSuccess, dispatch, onCloseClicked]);

	useEffect(() => {
		if (open) {
			const teamUsersArray = userState.users.map((user: TeamUsers) => {
				return {
					id: user.userId,
					name: user.userName,
					hasAcceptedTerms: user.hasAcceptedTerms,
				};
			});

			// order alphabetically
			teamUsersArray.sort((a, b) => a.name.localeCompare(b.name));

			const filteredPostRecipients = postRecipients
				? teamUsersArray?.filter((r: UserIdName) => {
						return postRecipients.some((user: UserIdName) => user.id === r.id && r.id !== userState.userId);
				  })
				: [];

			setTeamUsers(teamUsersArray);
			setInitialRecipients(filteredPostRecipients);
			setRecipients(filteredPostRecipients);
		}
	}, [open, userState.users, userState.userId, postRecipients]);

	useEffect(() => {
		if (open) {
			setTokens(0);
			setMessage('');
			unacceptedUsers.current = [];
		}
	}, [open]);

	return (
		<ModalWrapper open={open} onCloseClicked={onCloseClicked} allowClickOutsideToClose={allowClickOutsideToClose}>
			<Grid container direction='column' alignItems='center' className='send-tokens'>
				<Tooltip
					open={!iconMovement && tooltipOpen}
					onOpen={() => setToolTipOpen(true)}
					onClose={() => setToolTipOpen(false)}
					title='Think you can catch me? Betcha cant... click to find out 🥚'
				>
					<img
						onMouseEnter={event => modalRandomMovement(event, iconMovement)}
						onClick={() => toggleIconMovement()}
						src={TokenIcon}
						className='send-tokens__icon'
						alt='toa-token'
						id='send-tokens-icon'
						// added element level styles so that the first movement doest "jump"
						style={{
							right: '0px',
							top: '0px',
						}}
					/>
				</Tooltip>
				<div className='send-tokens__title'>Send tokens</div>

				<Grid container item className={`${unacceptedUsers.current.length > 0 ? 'send-tokens__innergrid--less-margin' : 'send-tokens__innergrid'}`}>
					<Grid item xs={4} className='send-tokens__innergrid-token-item'>
						<TextField
							fullWidth
							id='tokens-amount'
							type='number'
							label='Tokens'
							variant='outlined'
							error={tokenError}
							value={tokens === 0 ? '' : tokens}
							onChange={event => setTokenValue(event)}
						/>
						<div className='send-tokens__subtext'>Required</div>
					</Grid>
					<Grid item xs={8}>
						<Autocomplete
							multiple
							debug
							limitTags={2}
							defaultValue={initialRecipients}
							id='tokens-recipients'
							options={teamUsers.filter((user: UserIdName) => user.id !== userState.userId)}
							filterSelectedOptions
							onChange={(event, selectedUsers) => setTokenRecipients(selectedUsers)}
							getOptionLabel={(user: UserIdName) => user.name}
							getOptionSelected={(option: UserIdName, value: UserIdName) => option.id === value.id}
							disableCloseOnSelect={true}
							renderInput={params => (
								<TextField {...params} variant='outlined' label='Recipient' placeholder='Recipient' error={recipientsError} />
							)}
						/>
						<div className='send-tokens__subtext'>Required (at least 1 recipient)</div>
					</Grid>
					<Grid item xs={12} className='send-tokens__innergrid-message-item'>
						<TextField
							rows={4}
							multiline
							value={message}
							fullWidth={true}
							label='Message'
							variant='outlined'
							id='toknes-text'
							onChange={event => setMessageValue(event)}
						/>
					</Grid>
				</Grid>
				{unacceptedUsers.current.length > 0 && (
					<Alert severity='warning' className='send-tokens__alert'>
						{unacceptedUsers.current.join(', ')} {unacceptedUsers.current.length > 1 ? 'are' : 'is'} unable to receive tokens because{' '}
						{unacceptedUsers.current.length > 1 ? 'they have' : 'this user has'} not accepted the Terms of Use.
					</Alert>
				)}
				<div className={`${unacceptedUsers.current.length > 0 ? 'send-tokens__button-container--less-margin' : 'send-tokens__button-container'}`}>
					<Button variant='contained' color='primary' className='send-tokens__button-container--cancel' onClick={onCloseClicked}>
						cancel
					</Button>
					<LoaderButton type='button' {...loaderButtonProps}>
						Send
					</LoaderButton>
				</div>
			</Grid>
		</ModalWrapper>
	);
};

export default SendTokensModal;
